import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { each, isArray } from 'lodash';
import { ButtonBanner } from 'src/app/interfaces/banner/button-banner';
import { NotificationBanner } from 'src/app/interfaces/banner/notification-banner';
import { NotificationRecipient } from 'src/app/interfaces/models/notification-recipient';
import { WidgetInfoGroup } from 'src/app/interfaces/widget-info-group';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

import { WidgetInfo } from '../../interfaces/widget-info';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DashboardComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private NotificationService: NotificationService,
    private AuthService: AuthService,
    private UtilityService: UtilityService,
    private ToastService: ToastService,
    private router: Router
  ) {}

  currentColumnNumber: number = 0;
  @Input() notificationBanners: Set<NotificationBanner> =
    new Set<NotificationBanner>();
  @Input() buttonBanners: Set<ButtonBanner> = new Set<ButtonBanner>();

  @Input() CompanyId?: number;
  @Input() VendorId?: number;

  @Input() dashboardWidgetInfoGroup: WidgetInfoGroup = {
    widgetInfoCollection: []
  };
  showWidgetsButton: boolean = true;
  // Type guard, used to identify if an object is a widgetInfo interface
  isWidgetInfoGroup(
    widgetInfo: WidgetInfo | WidgetInfoGroup
  ): widgetInfo is WidgetInfoGroup {
    const widgetInfoGroup = widgetInfo as WidgetInfoGroup;
    return widgetInfoGroup.widgetInfoCollection !== undefined;
  }

  archiveBanner(notificationBanner: NotificationBanner) {
    // Remove the notification banner from the collection. Send an archive request to the server.
    this.notificationBanners.delete(notificationBanner);

    const notificationRecipient: NotificationRecipient | undefined =
      notificationBanner.NotificationRecipient;
    if (notificationRecipient) {
      this.NotificationService.archiveNotificationRecipient(
        notificationRecipient
      ).subscribe();
    } else {
      throw new Error('Error. Notification was not properly dismissed.');
    }
  }

  bannerButtonClick(action: { navigate: (string | number)[] }) {
    const { navigate } = action;
    if (navigate) {
      this.router.navigate(navigate);
    }
  }

  // Poll for notifications
  poll() {
    return this.NotificationService.pollForNotifications().subscribe({
      next: (data: any) => {
        this.notificationBanners.clear();
        each(data, (d, index) => {
          const { NotificationRecipients } = d;
          if (!isArray(NotificationRecipients)) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Notification Recipient missing from notification.',
              { returnError: true }
            );
          }
          const NotificationRecipient: NotificationRecipient =
            NotificationRecipients[0];
          const banner = this.UtilityService.mapValuesToNotificationBanner(
            'primary',
            d.title,
            d.bodyText,
            NotificationRecipient
          );
          this.notificationBanners.add(banner);
        });
        this._cdr.markForCheck();
      },
      error: (data: any) => {}
    });
  }
  // Recursive function to toggle visibility of widgets in nested groups
  toggleWidgetVisibility(widgetCollection: any[]) {
    each(widgetCollection, (widgetInfo) => {
      if (this.isWidgetInfoGroup(widgetInfo)) {
        this.toggleWidgetVisibility(widgetInfo.widgetInfoCollection);
      } else {
        widgetInfo.show = true;
      }
    });
  }
  showAllWidgets() {
    this.showWidgetsButton = false;
    // Call the recursive function
    this.toggleWidgetVisibility(
      this.dashboardWidgetInfoGroup.widgetInfoCollection
    );
    this._cdr.markForCheck();
  }

  ngOnInit(): void {
    // Every 5 minutes, we'll make a call out to the database to pull in any notifications
    // When this is utilized, we need to refactor it to not poll so much. Define as global variable.
    // if (this.AuthService.isAuthenticated()) {
    //   this.poll();
    //   setInterval(() => this.poll(), 300000);
    // }
    // Check if the user is an Admin to show/hide the show all widgets button.
    this.AuthService.getUserType().subscribe((userType) => {
      if (userType !== 'Admin') {
        this.showWidgetsButton = false;
      }
      this._cdr.markForCheck();
    });
  }
}
